import { theme } from '@config/theme';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { FormControl, IconButton } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import classNames from 'classnames';
import React, { FC, useMemo, useState } from 'react';
import './index.css';

interface CustomInputProps {
	id?: string;
	label?: string;
	size?: 'small' | 'medium';
	variant?: 'standard' | 'filled' | 'outlined';
	icon?: React.ReactNode;
	endIcon?: React.ReactNode;
	value?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	required?: boolean;
	type?: React.InputHTMLAttributes<unknown>['type'];
	error?: boolean | string;
	touched?: boolean;
	renderRules?: boolean;
	isRequired?: boolean;
	autoComplete?: string;
	readonly?: boolean;
	className?: string;
	disabled?: boolean;
	inputProps?: React.InputHTMLAttributes<unknown>;
	rightPadding?: string;
	leftPadding?: string;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
	click?: () => void;
	onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const TaInput: FC<CustomInputProps> = ({
	id,
	label,
	variant = 'outlined',
	icon,
	autoComplete = 'on',
	value,
	inputProps,
	onChange,
	required,
	size,
	error,
	touched,
	isRequired,
	type,
	endIcon,
	readonly,
	onBlur,
	onFocus,
	click,
	onKeyPress,
	rightPadding,
	leftPadding,
	disabled,
}) => {
	const [showPassword, setShowPassword] = useState(false);
	const [iconColor, setIconColor] = useState(theme.palette.custom.gray);

	const handleClickShowPassword = () => setShowPassword((show) => !show);

	const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		onBlur && onBlur(event);
		setIconColor(theme.palette.custom.gray);
	};

	const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
		onFocus && onFocus(event);
		setIconColor(theme.palette.custom.teal);
	};

	const isError = useMemo(() => touched && !!error, [touched, error]);

	const renderStartAdornment = () => {
		if (!icon) return null;

		return (
			<InputAdornment
				position="start"
				sx={{ color: isError ? theme.palette.custom.brightRed : iconColor }}
			>
				{icon}
			</InputAdornment>
		);
	};

	const renderEndAdornment = () => {
		if (type === 'password') {
			return (
				<InputAdornment position="end">
					<IconButton
						aria-label={showPassword ? 'hide the password' : 'display the password'}
						onClick={handleClickShowPassword}
						onMouseDown={handleMouseDownPassword}
						onMouseUp={handleMouseUpPassword}
						edge="end"
						className={classNames({
							'!text-error': touched && isError,
							'!text-secondary': !touched || !isError,
						})}
					>
						{showPassword ? (
							<VisibilityOffOutlinedIcon
								sx={{ color: isError ? theme.palette.custom.brightRed : iconColor }}
							/>
						) : (
							<VisibilityOutlinedIcon
								sx={{ color: isError ? theme.palette.custom.brightRed : iconColor }}
							/>
						)}
					</IconButton>
				</InputAdornment>
			);
		}
		if (endIcon) {
			return (
				<InputAdornment
					position="end"
					sx={{ color: isError ? theme.palette.custom.brightRed : iconColor }}
				>
					{endIcon}
				</InputAdornment>
			);
		}

		return null;
	};

	return (
		<FormControl error={isError} fullWidth>
			<TextField
				disabled={disabled}
				onClick={click}
				required={required}
				autoComplete={autoComplete}
				error={isError}
				id={id}
				inputProps={inputProps}
				className="roboto-regular"
				label={
					<>
						{label}
						{isRequired && <span className="text-error ml-[4px]">*</span>}
					</>
				}
				sx={{
					'& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
						borderWidth: '2px',
						borderColor: theme.palette.custom.brightRed,
					},
					'& .MuiInputBase-root': {
						paddingRight: rightPadding,
						paddingLeft: leftPadding,
					},
				}}
				variant={variant}
				value={value ?? ''}
				onChange={onChange}
				onBlur={handleBlur}
				onFocus={handleFocus}
				title=""
				size={size}
				slotProps={{
					input: {
						startAdornment: renderStartAdornment(),
						endAdornment: renderEndAdornment(),
						readOnly: readonly,
					},
				}}
				type={showPassword ? 'text' : type}
				onKeyDown={onKeyPress}
			/>
		</FormControl>
	);
};

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Nullable } from '@utils/util.types';
import { Dayjs } from 'dayjs';

interface TaDatePickerProps {
	id?: string;
	label?: string;
	value: Nullable<Dayjs>;
	onChange: (value: Nullable<Dayjs>) => void;
	error?: boolean | string;
	touched?: boolean;
	isRequired?: boolean;
	size?: 'small' | 'medium';
	readonly?: boolean;
	minDate?: Nullable<Dayjs>;
}

export const TaDatePicker: FC<TaDatePickerProps> = ({
	id,
	label,
	value,
	onChange,
	error,
	touched,
	isRequired,
	size = 'medium',
	readonly,
	minDate,
}) => {
	const isError = touched && !!error;

	return (
		<FormControl error={isError} fullWidth>
			<DatePicker
				minDate={minDate || undefined}
				value={value || undefined}
				onChange={onChange}
				disabled={readonly}
				slotProps={{
					textField: {
						id,
						size,
						error: isError,
						className: 'roboto-regular',
						label: (
							<>
								{label}
								{isRequired && <span className="text-error ml-[4px]">*</span>}
							</>
						),
						sx: {
							'& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
								borderWidth: '2px',
								borderColor: theme.palette.custom.brightRed,
							},
						},
					},
				}}
			/>
		</FormControl>
	);
};
